/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { WeakRegistry } from '@blade/std-lib'
import { reatomLimitOrderSell } from '@root/entities/limit-order/atoms/reatom-limit-order-sell'
import { Network } from '@root/entities/network'
import { PairAddress } from '@root/entities/pair'
import { pairRegistry } from '@root/entities/pair/atoms'
import { userPositionUpdateEvent } from '@root/entities/realtime'
import { WalletAddress, WalletId, walletsByIdAtom } from '@root/entities/wallet'
import { reatomBigNumber } from '@root/shared/lib'

import { PositionId } from '../model'

import { reatomPositionPnl } from './reatom-position-pnl'

import type { UserPositionLight } from '@blade/blockchain-types'
import type { PairModel } from '@root/entities/pair'
import type { TradeApi, UserApi } from '@root/shared/api'

type PositionDto = UserApi.UserPositionDto | UserPositionLight

class WalletPositionModelClass {}

export const reatomWalletPosition_ = ({
  dto,
  pair,
}: {
  dto: PositionDto
  pair: PairModel
}) => {
  const id = PositionId(dto.id)
  const pairAddress = pair.address
  const walletId = WalletId(dto.walletId)
  const walletAddress = WalletAddress(dto.walletAddress)
  const network = Network.fromDto(dto.network)
  const createdAt = new Date(dto.createdAt)

  //#region State
  const updatedAtAtom = atom(new Date(dto.updatedAt))
  const closedAtAtom = atom(dto.closedAt ? new Date(dto.closedAt) : undefined)

  const entryPriceUsdAtom = reatomBigNumber(dto.entryPrice.priceUsd)
  const entryPriceNativeAtom = reatomBigNumber(dto.entryPrice.priceNative)
  const entryMarketCapUsdAtom = reatomBigNumber(dto.entryPrice.marketCap)

  const targetPriceUsdAtom = atom(
    dto.targetPrice?.priceUsd ? BigNumber(dto.targetPrice.priceUsd) : undefined,
  )
  const targetPriceNativeAtom = atom(
    dto.targetPrice?.priceNative
      ? BigNumber(dto.targetPrice.priceNative)
      : undefined,
  )
  const targetMarketCapUsdAtom = atom(
    dto.targetPrice?.marketCap
      ? BigNumber(dto.targetPrice.marketCap)
      : undefined,
  )

  const closePriceUsdAtom = atom(
    dto.closePrice?.priceUsd ? BigNumber(dto.closePrice.priceUsd) : undefined,
  )
  const closePriceNativeAtom = atom(
    dto.closePrice?.priceNative
      ? BigNumber(dto.closePrice.priceNative)
      : undefined,
  )
  const closeMarketCapUsdAtom = atom(
    dto.closePrice?.marketCap ? BigNumber(dto.closePrice.marketCap) : undefined,
  )

  const hiddenAtom = atom(dto.hidden)

  const boughtSizeUsdAtom = reatomBigNumber(dto.boughtSizeUSD)
  const boughtSizeNativeAtom = reatomBigNumber(dto.boughtSizeNative)
  const boughtSizeCurrencyAtom = reatomBigNumber(dto.boughtSizeTokens)
  const boughtTransactionsAtom = reatomBigNumber(dto.boughtTransactions)

  const soldSizeUsdAtom = reatomBigNumber(dto.soldSizeUSD)
  const soldSizeNativeAtom = reatomBigNumber(dto.soldSizeNative)
  const soldSizeCurrencyAtom = reatomBigNumber(dto.soldSizeTokens)
  const soldTransactionsAtom = reatomBigNumber(dto.soldTransactions)

  const remainingSizeCurrencyAtom = reatomBigNumber(dto.remainingSizeTokens)

  const sellLimitOrderAtom = atom(
    dto.sellLimitOrder
      ? reatomLimitOrderSell({
          dto: dto.sellLimitOrder as TradeApi.LimitOrderSellDto,
          pair,
        })
      : undefined,
  )
  //#endregion

  //#region Computed
  const priceAtom = pair.priceRealtime
  const remainingSizeUsdAtom = atom((ctx) => {
    const price = ctx.spy(priceAtom)
    const size = ctx.spy(remainingSizeCurrencyAtom)
    return price.priceUsd.multipliedBy(size)
  })
  const remainingSizeNativeAtom = atom((ctx) => {
    const price = ctx.spy(priceAtom)
    const size = ctx.spy(remainingSizeCurrencyAtom)
    return price.priceNative.multipliedBy(size)
  })

  const pnl = reatomPositionPnl({
    remainingSizeCurrencyAtom: remainingSizeCurrencyAtom,
    priceAtom,
    entryPriceUsdAtom,
    entryPriceNativeAtom,
    soldSizeUsdAtom,
    soldSizeNativeAtom,
    soldSizeCurrencyAtom,
  })
  const walletAtom = atom((ctx) => {
    const wallets = ctx.spy(walletsByIdAtom)
    return wallets.get(walletId)
  })
  //#endregion

  const updateFromDtoAction = action((ctx, dto: PositionDto) => {
    hiddenAtom(ctx, dto.hidden)

    updatedAtAtom(ctx, new Date(dto.updatedAt))
    closedAtAtom(ctx, dto.closedAt ? new Date(dto.closedAt) : undefined)

    boughtSizeUsdAtom.set(ctx, dto.boughtSizeUSD)
    boughtSizeNativeAtom.set(ctx, dto.boughtSizeNative)
    boughtSizeCurrencyAtom.set(ctx, dto.boughtSizeTokens)
    boughtTransactionsAtom.set(ctx, dto.boughtTransactions)
    soldSizeUsdAtom.set(ctx, dto.soldSizeUSD)
    soldSizeNativeAtom.set(ctx, dto.soldSizeNative)
    soldSizeCurrencyAtom.set(ctx, dto.soldSizeTokens)
    soldTransactionsAtom.set(ctx, dto.soldTransactions)

    entryPriceUsdAtom.set(ctx, BigNumber(dto.entryPrice.priceUsd))
    entryPriceNativeAtom.set(ctx, BigNumber(dto.entryPrice.priceNative))
    entryMarketCapUsdAtom.set(ctx, BigNumber(dto.entryPrice.marketCap))

    targetPriceUsdAtom(
      ctx,
      dto.targetPrice?.priceUsd
        ? BigNumber(dto.targetPrice.priceUsd)
        : undefined,
    )
    targetPriceNativeAtom(
      ctx,
      dto.targetPrice?.priceNative
        ? BigNumber(dto.targetPrice.priceNative)
        : undefined,
    )
    targetMarketCapUsdAtom(
      ctx,
      dto.targetPrice?.marketCap
        ? BigNumber(dto.targetPrice.marketCap)
        : undefined,
    )

    closePriceUsdAtom(
      ctx,
      dto.closePrice?.priceUsd ? BigNumber(dto.closePrice.priceUsd) : undefined,
    )
    closePriceNativeAtom(
      ctx,
      dto.closePrice?.priceNative
        ? BigNumber(dto.closePrice.priceNative)
        : undefined,
    )
    closeMarketCapUsdAtom(
      ctx,
      dto.closePrice?.marketCap
        ? BigNumber(dto.closePrice.marketCap)
        : undefined,
    )

    remainingSizeCurrencyAtom.set(ctx, dto.remainingSizeTokens)

    if (typeof dto.pair === 'object') {
      pair.updateFromDto(ctx, dto.pair)
    }

    const sellLimitOrder = ctx.get(sellLimitOrderAtom)
    if (dto.sellLimitOrder) {
      if (sellLimitOrder) {
        sellLimitOrder.updateFromDto(
          ctx,
          dto.sellLimitOrder as TradeApi.LimitOrderSellDto,
        )
      } else {
        sellLimitOrderAtom(
          ctx,
          reatomLimitOrderSell({
            dto: dto.sellLimitOrder as TradeApi.LimitOrderSellDto,
            pair,
          }),
        )
      }
    }
  })

  const model = {
    id,

    walletId,
    walletAddress,
    walletAtom,

    network,
    pairAddress,
    pair,
    hidden: hiddenAtom,

    createdAt,
    closedAt: closedAtAtom,
    sellLimitOrder: sellLimitOrderAtom,

    entryPriceUsd: entryPriceUsdAtom,
    entryPriceNative: entryPriceNativeAtom,
    entryMarketCapUsd: entryMarketCapUsdAtom,

    targetPriceUsd: targetPriceUsdAtom,
    targetPriceNative: targetPriceNativeAtom,
    targetMarketCapUsd: targetMarketCapUsdAtom,

    closePriceUsd: closePriceUsdAtom,
    closePriceNative: closePriceNativeAtom,
    closeMarketCapUsd: closeMarketCapUsdAtom,

    boughtSizeUsd: boughtSizeUsdAtom,
    boughtSizeNative: boughtSizeNativeAtom,
    boughtSizeCurrency: boughtSizeCurrencyAtom,
    boughtTransactions: boughtTransactionsAtom,

    soldSizeUsd: soldSizeUsdAtom,
    soldSizeNative: soldSizeNativeAtom,
    soldSizeCurrency: soldSizeCurrencyAtom,
    soldTransactions: soldTransactionsAtom,

    remainingSizeUsd: remainingSizeUsdAtom,
    remainingSizeNative: remainingSizeNativeAtom,
    remainingSizeCurrency: remainingSizeCurrencyAtom,

    ...pnl,

    updateFromDto: updateFromDtoAction,
  }

  Object.setPrototypeOf(model, WalletPositionModelClass.prototype)

  return model
}

export interface WalletPositionModel
  extends ReturnType<typeof reatomWalletPosition_> {}

const walletPositionWeakRegistry = new WeakRegistry<
  string,
  WalletPositionModel
>()
const mkKey = (
  walletId: WalletId,
  pairAddress: PairAddress,
  network: Network.Network,
) => {
  return `${network}:${pairAddress}:${walletId}`
}

export const reatomWalletPosition = ({
  dto,
  pair,
}: {
  dto: UserApi.UserPositionDto | UserPositionLight
  pair: PairModel
}) => {
  const key = mkKey(WalletId(dto.walletId), pair.address, pair.network)
  return walletPositionWeakRegistry.getOrCreate(key, () =>
    reatomWalletPosition_({ dto, pair }),
  )
}

reatomWalletPosition.create = action((ctx, dto: UserApi.UserPositionDto) => {
  return reatomWalletPosition({
    dto,
    pair: pairRegistry.resolveFromDto(ctx, dto.pair),
  })
})

userPositionUpdateEvent.onCall((ctx, update) => {
  const key = mkKey(
    WalletId(update.walletId),
    PairAddress(update.pair),
    Network.fromDto(update.network),
  )
  const model = walletPositionWeakRegistry.get(key)
  if (!model) {
    return
  }
  model.updateFromDto(ctx, update)
})
